/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';

import Loader from '../../../../elements/Loader';
import FormBoolean from '../../../../elements/formElements/FormBoolean';
import FormGroupNumber from '../../../../elements/formElements/FormGroupNumber';
import SubmitButton from '../../../../elements/formElements/SubmitButton';
import axiosCRM from '../../../../services/MyAxios';
import { API_CASINO_SETTINGS_TEMPLATE, API_CASINO_SETTINGS_TEMPLATE_UPDATE } from '../../Helpers/CrmApiUrls';
import { CASINO_SETTINGS } from '../../Helpers/CrmUrls';

import FormSwitchSelector from 'crm/elements/formElements/FormSwitchSelector';

interface Settings {
    show_game_creators_slider: number;
    games_sliders_enable_autoplay: number;
    games_sliders_autoplay_speed: number;
    game_category_block_template: 'square' | 'rectangle';
}

function TemplateSettings() {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<Settings>({
        show_game_creators_slider: null,
        games_sliders_enable_autoplay: null,
        games_sliders_autoplay_speed: null,
        game_category_block_template: 'square',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = (e): void => {
        e.preventDefault();

        axiosCRM
            .post(API_CASINO_SETTINGS_TEMPLATE_UPDATE, settings)
            .then((response) => {
                if (response.status === 204) {
                    swal({
                        title: 'Success',
                        text: 'Template settings updated correctly',
                        icon: 'success',
                    }).then(() => {
                        history.push(CASINO_SETTINGS);
                    });
                }
            })
            .catch((error) => {
                if (!error.response) {
                    setErrors({ message: error.message });
                } else if (error.response.status !== 500) {
                    setErrors(error.response.data.errors);
                } else {
                    swal({
                        title: 'Error',
                        text: 'Server error',
                        icon: 'error',
                    });
                }
            });
    };

    const handleFieldChange = (fieldName: string, value: string | number) => {
        setSettings({ ...settings, [fieldName]: value });
    };

    const getCurrentSettings = (): void => {
        axiosCRM
            .get(API_CASINO_SETTINGS_TEMPLATE)
            .then((response) => {
                setSettings(response.data);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'Server error',
                    icon: 'error',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getCurrentSettings();
    }, []);

    const getContent = (): JSX.Element => {
        return (
            <form className="form-flex" onSubmit={handleSubmit}>
                <FormBoolean
                    label="Enable autoplay of game silders"
                    handleChange={(value) => handleFieldChange('games_sliders_enable_autoplay', value)}
                    currentValue={settings.games_sliders_enable_autoplay}
                    required
                    field="games_sliders_enable_autoplay"
                    error={errors.games_sliders_enable_autoplay ?? undefined}
                />
                <FormGroupNumber
                    name="games_sliders_autoplay_speed"
                    value={settings.games_sliders_autoplay_speed}
                    label="Autoplay speed of games sliders (ms)"
                    error={errors.games_sliders_autoplay_speed ?? undefined}
                    required
                    minValue={1000}
                    maxValue={60000}
                    stepValue={100}
                    handleChange={(event) => handleFieldChange('games_sliders_autoplay_speed', event.target.value)}
                />
                <FormBoolean
                    label="Display providers slider on homepage"
                    handleChange={(value) => handleFieldChange('show_game_creators_slider', value)}
                    currentValue={settings.show_game_creators_slider}
                    required
                    field="show_game_creators_slider"
                    error={errors.show_game_creators_slider ?? undefined}
                />
                <FormSwitchSelector
                    label="Game category block template"
                    handleChange={(value) => handleFieldChange('game_category_block_template', value)}
                    options={[
                        {
                            label: 'Square',
                            value: 'square',
                            selectedBackgroundColor: '#3f8f29',
                        },
                        {
                            label: 'Rectangle',
                            value: 'rectangle',
                            selectedBackgroundColor: '#3f8f29',
                        },
                    ]}
                    currentValue={settings.game_category_block_template ?? 'square'}
                    required
                    field="game_category_block_template"
                    error={errors.game_category_block_template ?? undefined}
                />
                <SubmitButton isLoading={isLoading} onClick={handleSubmit} label="Save" />
            </form>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}

export default TemplateSettings;
